import { isEqual, uniqBy } from 'lodash-es';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useWatch } from '@/package';
import {
  useLabelingBoxStore,
  usePcdRenderingStore,
  useReviewStore,
  useTaskFrameStore,
  useToolSettingStore,
} from '@/store';
import type { BoxWithLabel } from '@/type';
import {
  type NEW__LabelingBox,
  NEW__labelingBoxPool,
  isEqualBoxList,
} from '@/util';

export const useLabelingBoxPool = () => {
  const pcdIndex = usePcdRenderingStore(state => state.pcdIndex);
  const labelingBoxOpacities = useToolSettingStore(
    state => state.labelingBoxOpacities,
  );
  const [pcdIndexBoxListMap, selectedBoxIdMap] = useLabelingBoxStore(state => [
    state.pcdIndexBoxListMap,
    state.selectedBoxIdMap,
  ]);
  const selectedTask = useTaskFrameStore(state => state.selectedTask);
  const pcdIndexReviewBoxListMap = useReviewStore(
    state => state.pcdIndexReviewBoxListMap,
  );
  const reviewBoxList = useMemo(
    () => useReviewStore.getState().getCurrentPcdIndexReviewBoxList(),
    [pcdIndexReviewBoxListMap],
  );

  const [labelingBoxList, setLabelingBoxList] = useState(
    [] as NEW__LabelingBox[],
  );
  const [selectedLabelingBoxList, setSelectedLabelingBoxList] = useState(
    [] as NEW__LabelingBox[],
  );
  const renderedLabelingBoxListRef = useRef([] as BoxWithLabel[]);
  const labelingBoxListRef = useRef([] as NEW__LabelingBox[]);

  const boxList = useMemo(
    () =>
      uniqBy(
        useLabelingBoxStore
          .getState()
          .getCurrentBoxList()
          .concat(pcdIndexBoxListMap[pcdIndex] ?? []),
        box => box.id,
      ),
    [pcdIndex, pcdIndexBoxListMap],
  );

  const applyBoxStyle = (labelingBox: NEW__LabelingBox) => {
    const { background, border } = useToolSettingStore
      .getState()
      .getOpacityByLabelId(labelingBox.label);
    const currentSelectedBoxIdMap =
      useLabelingBoxStore.getState().selectedBoxIdMap;

    // 선택 상태 적용
    if (currentSelectedBoxIdMap[labelingBox.id]) {
      labelingBox.select();
    } else {
      labelingBox.deselect();

      labelingBox.updateBoxOpacity(background);
      labelingBox.updateLineOpacity(border);
    }
  };

  useWatch(
    [
      boxList,
      labelingBoxOpacities,
      reviewBoxList,
      selectedTask?.extendClass,
    ] as const,
    (
      [newBoxList, newOpacity, newReviewBoxList, newExtendClass],
      [oldBoxList, oldOpacity, oldReviewBoxList, oldExtendClass],
    ) => {
      if (
        (isEqual(newOpacity, oldOpacity) &&
          isEqualBoxList(newBoxList, oldBoxList) &&
          isEqualBoxList(newReviewBoxList, oldReviewBoxList) &&
          isEqual(newExtendClass, oldExtendClass)) ||
        !newExtendClass
      )
        return;

      const labelingBoxList = newBoxList.map(box => {
        box.color =
          newExtendClass.find(item => +item.id === +box.label)?.color ??
          '#ffffff';

        return box;
      });
      const noTouchedReviewBoxList = newReviewBoxList.filter(
        box => !labelingBoxList.find(b => b.id === box.id),
      );

      const renderedLabelingBoxList = [
        ...labelingBoxList,
        ...noTouchedReviewBoxList,
      ];

      if (
        isEqual(newOpacity, oldOpacity) &&
        isEqualBoxList(
          renderedLabelingBoxList,
          renderedLabelingBoxListRef.current,
        )
      ) {
        setLabelingBoxList(labelingBoxListRef.current);
        return;
      }

      NEW__labelingBoxPool.setLabelingBoxList(renderedLabelingBoxList);

      const result = NEW__labelingBoxPool.getLabelingBoxList(
        renderedLabelingBoxList.length,
      );

      result.forEach(applyBoxStyle);

      renderedLabelingBoxListRef.current = renderedLabelingBoxList;
      labelingBoxListRef.current = result;

      setLabelingBoxList(result);
    },
    {},
  );

  // 선택 상태 업데이트
  useEffect(() => {
    const selectedList: NEW__LabelingBox[] = [];

    labelingBoxList.forEach(box => {
      if (selectedBoxIdMap[box.id]) {
        box.select();
        selectedList.push(box);
      } else {
        box.deselect();
        // deselect 후 store의 opacity 값으로 복원
        const { background, border } = useToolSettingStore
          .getState()
          .getOpacityByLabelId(box.label);
        box.updateBoxOpacity(background);
        box.updateLineOpacity(border);
      }
    });

    setSelectedLabelingBoxList(selectedList);
  }, [labelingBoxList, selectedBoxIdMap]);

  useEffect(() => {
    NEW__labelingBoxPool.releaseAllLabelingBox();
  }, [pcdIndex]);

  return { labelingBoxList, selectedLabelingBoxList };
};
